// ContactModal.js - Form semplice per richiesta demo e prezzi
import React, { useState, useEffect } from "react"
import { FaTimes } from "react-icons/fa"
import { getTranslations } from "./components/translations"

const ContactModal = ({
  isOpen,
  onClose,
  selectedProduct,
  language = "it",
}) => {
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    azienda: "",
    messaggio: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState("")

  // Get translations
  const translations = getTranslations(language)

  // Reset modal state when closed
  useEffect(() => {
    if (!isOpen) {
      setSubmitSuccess(false)
      setSubmitError("")
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError("")

    try {
      // Prepara i dati da inviare
      const formDataToSend = {
        personalData: {
          nome: formData.nome,
          cognome: formData.cognome,
          email: formData.email,
          telefono: formData.telefono,
          azienda: formData.azienda,
          messaggio: formData.messaggio,
        },
        productData: selectedProduct
          ? {
              name: selectedProduct.name,
              code: selectedProduct.code,
              family: selectedProduct._family || selectedProduct.family,
            }
          : null,
        timestamp: new Date().toISOString(),
        language: language,
        currentUrl: window.location.href,
      }

      // Invia i dati al sito parent via PostMessage
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(
          {
            type: "CONFIGURATOR_DEMO_REQUEST",
            data: formDataToSend,
          },
          "*"
        )
        console.log("Dati inviati al sito parent:", formDataToSend)
      } else {
        console.log("Dati del form (modalità standalone):", formDataToSend)
      }

      // Simula un piccolo delay per UX
      await new Promise((resolve) => setTimeout(resolve, 1000))

      setSubmitSuccess(true)

      // Resetta il form dopo il successo
      setTimeout(() => {
        setSubmitSuccess(false)
        onClose()
        setFormData({
          nome: "",
          cognome: "",
          email: "",
          telefono: "",
          azienda: "",
          messaggio: "",
        })
      }, 2000)
    } catch (error) {
      console.error("Errore nell'invio del form:", error)
      setSubmitError(
        translations.contactModal?.errorMessage ||
          "Impossibile inviare il modulo. Si prega di riprovare."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  // Traduzioni per il form semplificato
  const formTranslations = {
    it: {
      title: "Richiedi informazioni",
      subtitle: "Compila il form per richiedere una demo o informazioni sui prezzi del configuratore",
      productInterest: "Prodotto di interesse",
      name: "Nome",
      surname: "Cognome",
      email: "Email",
      phone: "Telefono",
      company: "Azienda",
      message: "Messaggio",
      messagePlaceholder: "Scrivi qui la tua richiesta: informazioni sul prodotto, richiesta demo, preventivo...",
      cancel: "Annulla",
      submit: "Invia richiesta",
      submitting: "Invio in corso...",
      successTitle: "Richiesta inviata!",
      successMessage: "Ti contatteremo al più presto.",
      required: "Campo obbligatorio",
    },
    en: {
      title: "Request information",
      subtitle: "Fill out the form to request a demo or pricing information",
      productInterest: "Product of interest",
      name: "Name",
      surname: "Surname",
      email: "Email",
      phone: "Phone",
      company: "Company",
      message: "Message",
      messagePlaceholder: "Write your request here: product information, demo request, quote...",
      cancel: "Cancel",
      submit: "Send request",
      submitting: "Sending...",
      successTitle: "Request sent!",
      successMessage: "We will contact you soon.",
      required: "Required field",
    },
    fr: {
      title: "Demander des informations",
      subtitle: "Remplissez le formulaire pour demander une démo ou des informations sur les prix",
      productInterest: "Produit d'intérêt",
      name: "Prénom",
      surname: "Nom",
      email: "Email",
      phone: "Téléphone",
      company: "Entreprise",
      message: "Message",
      messagePlaceholder: "Écrivez votre demande ici: informations sur le produit, demande de démo, devis...",
      cancel: "Annuler",
      submit: "Envoyer la demande",
      submitting: "Envoi en cours...",
      successTitle: "Demande envoyée!",
      successMessage: "Nous vous contacterons bientôt.",
      required: "Champ obligatoire",
    },
    de: {
      title: "Informationen anfordern",
      subtitle: "Füllen Sie das Formular aus, um eine Demo oder Preisinformationen anzufordern",
      productInterest: "Produkt von Interesse",
      name: "Vorname",
      surname: "Nachname",
      email: "E-Mail",
      phone: "Telefon",
      company: "Unternehmen",
      message: "Nachricht",
      messagePlaceholder: "Schreiben Sie hier Ihre Anfrage: Produktinformationen, Demo-Anfrage, Angebot...",
      cancel: "Abbrechen",
      submit: "Anfrage senden",
      submitting: "Wird gesendet...",
      successTitle: "Anfrage gesendet!",
      successMessage: "Wir werden Sie bald kontaktieren.",
      required: "Pflichtfeld",
    },
  }

  const t = formTranslations[language] || formTranslations.it

  return (
    <div className="ay-fixed ay-inset-0 ay-bg-black ay-bg-opacity-50 ay-flex ay-items-center ay-justify-center ay-z-[9999]">
      <div className="ay-bg-white ay-shadow-xl ay-w-full ay-max-w-lg ay-max-h-[90vh] ay-overflow-auto ay-mx-4">
        {/* Header */}
        <div className="ay-flex ay-justify-between ay-items-center ay-px-6 ay-py-4 ay-border-b ay-border-gray-200">
          <div>
            <h2 className="ay-text-xl ay-font-bold ay-text-gray-800 ay-font-sans">
              {t.title}
            </h2>
            <p className="ay-text-sm ay-text-gray-500 ay-mt-1">{t.subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="ay-text-gray-400 hover:ay-text-gray-600 ay-transition-colors ay-p-2"
          >
            <FaTimes size={18} />
          </button>
        </div>

        {submitSuccess ? (
          <div className="ay-text-center ay-py-16 ay-px-6">
            <div className="ay-bg-green-100 ay-text-green-600 ay-rounded-full ay-w-16 ay-h-16 ay-flex ay-items-center ay-justify-center ay-mx-auto ay-mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="ay-h-8 ay-w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="ay-text-xl ay-font-semibold ay-mb-2 ay-font-sans">
              {t.successTitle}
            </h3>
            <p className="ay-text-gray-600">{t.successMessage}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="ay-p-6">
            {/* Product info (if available) */}
            {selectedProduct && (
              <div className="ay-mb-6 ay-p-4 ay-bg-gray-50 ay-border ay-border-gray-200 ay-rounded">
                <p className="ay-text-xs ay-text-gray-500 ay-uppercase ay-tracking-wide ay-mb-1">
                  {t.productInterest}
                </p>
                <p className="ay-font-semibold ay-text-gray-800">
                  {selectedProduct.lang?.[language] || selectedProduct.name}
                </p>
                {selectedProduct.code && (
                  <p className="ay-text-sm ay-text-gray-500">
                    {selectedProduct.code}
                  </p>
                )}
              </div>
            )}

            {/* Form fields */}
            <div className="ay-space-y-4">
              {/* Nome e Cognome */}
              <div className="ay-grid ay-grid-cols-2 ay-gap-4">
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    {t.name} *
                  </label>
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                    className="ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded ay-text-sm focus:ay-outline-none focus:ay-ring-1 focus:ay-ring-gray-400 focus:ay-border-gray-400"
                  />
                </div>
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    {t.surname} *
                  </label>
                  <input
                    type="text"
                    name="cognome"
                    value={formData.cognome}
                    onChange={handleChange}
                    required
                    className="ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded ay-text-sm focus:ay-outline-none focus:ay-ring-1 focus:ay-ring-gray-400 focus:ay-border-gray-400"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                  {t.email} *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded ay-text-sm focus:ay-outline-none focus:ay-ring-1 focus:ay-ring-gray-400 focus:ay-border-gray-400"
                />
              </div>

              {/* Telefono e Azienda */}
              <div className="ay-grid ay-grid-cols-2 ay-gap-4">
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    {t.phone}
                  </label>
                  <input
                    type="tel"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    className="ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded ay-text-sm focus:ay-outline-none focus:ay-ring-1 focus:ay-ring-gray-400 focus:ay-border-gray-400"
                  />
                </div>
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    {t.company}
                  </label>
                  <input
                    type="text"
                    name="azienda"
                    value={formData.azienda}
                    onChange={handleChange}
                    className="ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded ay-text-sm focus:ay-outline-none focus:ay-ring-1 focus:ay-ring-gray-400 focus:ay-border-gray-400"
                  />
                </div>
              </div>

              {/* Messaggio */}
              <div>
                <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                  {t.message}
                </label>
                <textarea
                  name="messaggio"
                  value={formData.messaggio}
                  onChange={handleChange}
                  rows="4"
                  placeholder={t.messagePlaceholder}
                  className="ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded ay-text-sm focus:ay-outline-none focus:ay-ring-1 focus:ay-ring-gray-400 focus:ay-border-gray-400 ay-resize-none"
                />
              </div>
            </div>

            {/* Error message */}
            {submitError && (
              <div className="ay-mt-4 ay-p-3 ay-bg-red-50 ay-text-red-700 ay-rounded ay-text-sm">
                {submitError}
              </div>
            )}

            {/* Buttons */}
            <div className="ay-flex ay-gap-3 ay-mt-6">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="ay-flex-1 ay-py-3 ay-px-4 ay-border ay-border-gray-300 ay-rounded-full ay-text-sm ay-font-medium ay-text-gray-700 hover:ay-bg-gray-50 ay-transition-colors disabled:ay-opacity-50"
              >
                {t.cancel}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="ay-flex-1 ay-py-3 ay-px-4 ay-bg-gray-800 ay-text-white ay-rounded-full ay-text-sm ay-font-medium hover:ay-bg-gray-700 ay-transition-colors disabled:ay-opacity-50 ay-flex ay-items-center ay-justify-center ay-gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="ay-animate-spin ay-h-4 ay-w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="ay-opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="ay-opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {t.submitting}
                  </>
                ) : (
                  t.submit
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default ContactModal
